'use client';

import { useMemo, useState } from 'react';
import {
  Wallet,
  HandCoins,
  Hourglass,
  ShieldAlert,
  Receipt,
  TrendingUp,
  Plus,
  Trash2,
  X,
  Shield,
  Car,
} from 'lucide-react';
import type { Booking, FleetExpense, Vehicle } from '@/types';
import { currencySymbol, formatMoney } from '@/lib/currency';
import { api, ApiError } from '@/lib/api';
import { useDeskSummary, useVehicleOptions } from '@/lib/desk/summary';
import { parseExpensePage } from '@/lib/ledger/ledger-catalog';
import { listPath } from '@/lib/query/page';
import { useInvalidateTenantResource } from '@/lib/query/scope';
import { useTenantQuery } from '@/lib/query/useTenantQuery';

interface FinanceModuleProps {
  bookings: Booking[];
  canViewProfits: boolean;
  currency?: string | null;
  headerQuery?: string;
}

type Period = 'day' | 'week' | 'month';

const CATEGORIES: FleetExpense['category'][] = [
  'maintenance',
  'assurance',
  'visite',
  'vignette',
  'fuel',
  'cleaning',
  'other',
];

function inPeriod(dateStr: string, period: Period): boolean {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return false;
  const now = new Date();
  const start = new Date(now);
  if (period === 'day') start.setHours(0, 0, 0, 0);
  if (period === 'week') start.setDate(now.getDate() - 7);
  if (period === 'month') start.setDate(now.getDate() - 30);
  return d >= start && d <= new Date(now.getTime() + 1000 * 60 * 60 * 24 * 365);
}

export function FinanceModule({ bookings, canViewProfits, currency, headerQuery = '' }: FinanceModuleProps) {
  const cur = currency ?? 'MAD';
  const invalidate = useInvalidateTenantResource();
  const limits = useDeskSummary();
  const vehicleOptions = useVehicleOptions();
  const vehicles = (vehicleOptions.data?.data ?? []) as unknown as Vehicle[];
  const expenseQuery = useTenantQuery(
    ['expenses', headerQuery],
    listPath('/api/expenses', { perPage: 20, q: headerQuery.trim() }),
    parseExpensePage,
    canViewProfits,
  );
  const expenses = (expenseQuery.data?.data ?? []) as FleetExpense[];
  const [period, setPeriod] = useState<Period>('month');
  const [showAdd, setShowAdd] = useState(false);
  const [label, setLabel] = useState('');
  const [amount, setAmount] = useState('');
  const [category, setCategory] = useState<FleetExpense['category']>('maintenance');
  const [vehicleId, setVehicleId] = useState('');
  const [date, setDate] = useState(() => new Date().toISOString().slice(0, 10));

  const validBookings = useMemo(
    () => bookings.filter((b) => b.status !== 'cancelled' && inPeriod(b.createdAt || b.pickupDate, period)),
    [bookings, period]
  );
  const periodExpenses = useMemo(() => expenses.filter((e) => inPeriod(e.date, period)), [expenses, period]);

  const revenue = limits.data?.summary.revenueMonth ?? validBookings.reduce((s, b) => s + b.totalPrice, 0);
  const avance = validBookings.reduce((s, b) => s + (b.avanceAmount ?? 0), 0);
  const reste = validBookings
    .filter((b) => ['pending', 'confirmed', 'active'].includes(b.status))
    .reduce((s, b) => s + (b.totalPrice - (b.avanceAmount ?? 0)), 0);
  const cautions = bookings
    .filter((b) => ['confirmed', 'active'].includes(b.status))
    .reduce((s, b) => s + (b.cautionAmount ?? 0), 0);
  const totalExpenses = periodExpenses.reduce((s, e) => s + e.amount, 0);
  const net = revenue - totalExpenses;

  // 14-day revenue bars
  const bars = useMemo(() => {
    const days: { key: string; label: string; value: number }[] = [];
    for (let i = 13; i >= 0; i--) {
      const d = new Date();
      d.setDate(d.getDate() - i);
      const key = d.toISOString().slice(0, 10);
      const value = bookings
        .filter((b) => b.status !== 'cancelled' && (b.createdAt || b.pickupDate).slice(0, 10) === key)
        .reduce((s, b) => s + b.totalPrice, 0);
      days.push({ key, label: d.toLocaleDateString('fr-MA', { day: '2-digit', month: '2-digit' }).slice(0, 5), value });
    }
    return days;
  }, [bookings]);
  const maxBar = Math.max(1, ...bars.map((b) => b.value));

  // Profit per vehicle
  const perVehicle = useMemo(() => {
    return vehicles
      .map((v) => {
        const rev = bookings
          .filter((b) => b.vehicleId === v.id && b.status !== 'cancelled')
          .reduce((s, b) => s + b.totalPrice, 0);
        const exp = expenses.filter((e) => e.vehicleId === v.id).reduce((s, e) => s + e.amount, 0);
        return { vehicle: v, revenue: rev, expenses: exp, margin: rev - exp };
      })
      .sort((a, b) => b.revenue - a.revenue);
  }, [vehicles, bookings, expenses]);

  async function addExpense() {
    const amt = parseFloat(amount);
    if (!label.trim() || isNaN(amt) || amt <= 0) return;
    const e = {
      label: label.trim(),
      amount: amt,
      category,
      vehicleId: vehicleId || undefined,
      date,
    };
    try {
      await api('/api/expenses', { method: 'POST', body: JSON.stringify(e) });
      await invalidate('expenses');
      await invalidate('desk-summary');
    } catch (error: unknown) {
      window.alert(error instanceof ApiError ? error.message : 'Could not save this expense.');
      return;
    }
    setLabel('');
    setAmount('');
    setShowAdd(false);
  }

  const kpis = [
    { icon: Wallet, label: 'Chiffre d’affaires', value: formatMoney(revenue, cur) },
    { icon: HandCoins, label: 'Avance encaissée', value: formatMoney(avance, cur) },
    { icon: Hourglass, label: 'Reste à encaisser', value: formatMoney(reste, cur) },
    { icon: ShieldAlert, label: 'Cautions détenues', value: formatMoney(cautions, cur) },
    { icon: Receipt, label: 'Dépenses', value: formatMoney(totalExpenses, cur) },
    ...(canViewProfits ? [{ icon: TrendingUp, label: 'Bénéfice net', value: formatMoney(net, cur) }] : []),
  ];

  return (
    <div className="space-y-4">
      {!canViewProfits && (
        <div className="flex items-center gap-3 rounded-3xl border border-warning/30 bg-warning/10 p-4">
          <span className="rounded-2xl bg-ink p-2 text-shell">
            <Shield size={16} />
          </span>
          <div>
            <p className="text-xs font-bold text-ink">Accès restreint — Front Desk ne peut pas voir les profits</p>
            <p className="text-[11px] text-muted">Seul l&apos;encaissement (avance / reste) est visible pour ce rôle.</p>
          </div>
        </div>
      )}

      {/* Period selector */}
      <div className="flex items-center gap-2">
        {(['day', 'week', 'month'] as Period[]).map((p) => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`rounded-2xl px-4 py-2 text-xs font-bold ${
              period === p ? 'bg-ink text-shell' : 'border border-line bg-card text-muted'
            }`}
          >
            {p === 'day' ? 'Jour' : p === 'week' ? '7 jours' : '30 jours'}
          </button>
        ))}
      </div>

      {/* KPI cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 lg:grid-cols-3">
        {kpis.map((k) => (
          <div key={k.label} className="rounded-3xl border border-line bg-card p-4">
            <p className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide text-muted">
              <k.icon size={14} className="text-[#FF8A00]" /> {k.label}
            </p>
            <p className="mt-1 text-lg font-black text-ink">{k.value}</p>
          </div>
        ))}
      </div>

      {/* 14-day chart */}
      <div className="rounded-3xl border border-line bg-card p-4">
        <p className="text-xs font-bold uppercase tracking-wide text-muted">Revenus — 14 derniers jours</p>
        <div className="mt-4 flex h-32 items-end gap-1.5">
          {bars.map((b) => (
            <div key={b.key} className="flex flex-1 flex-col items-center gap-1" title={`${b.label}: ${formatMoney(b.value, cur)}`}>
              <div
                className="w-full rounded-t-lg bg-brand"
                style={{ height: `${Math.max(4, (b.value / maxBar) * 110)}px`, opacity: b.value ? 1 : 0.12 }}
              />
              <span className="text-[9px] text-muted">{b.label.slice(0, 5)}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Profit per vehicle */}
      <div className="rounded-3xl border border-line bg-card p-4">
        <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
          <Car size={14} /> Rentabilité par véhicule
        </p>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[520px] text-xs">
            <thead>
              <tr className="text-left text-[11px] uppercase text-muted">
                <th className="py-2">Véhicule</th>
                <th className="py-2 text-right">Revenus</th>
                <th className="py-2 text-right">Dépenses</th>
                {canViewProfits && <th className="py-2 text-right">Marge</th>}
              </tr>
            </thead>
            <tbody>
              {perVehicle.map((r) => (
                <tr key={r.vehicle.id} className="border-t border-line">
                  <td className="py-2 font-semibold text-ink">{r.vehicle.name}</td>
                  <td className="py-2 text-right">{formatMoney(r.revenue, cur)}</td>
                  <td className="py-2 text-right text-muted">{formatMoney(r.expenses, cur)}</td>
                  {canViewProfits && (
                    <td className={`py-2 text-right font-bold ${r.margin >= 0 ? 'text-success' : 'text-danger'}`}>
                      {formatMoney(r.margin, cur)}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Expense log */}
      <div className="rounded-3xl border border-line bg-card p-4">
        <div className="flex items-center justify-between">
          <p className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide text-muted">
            <Receipt size={14} /> Dépenses ({expenses.length})
          </p>
          <button
            onClick={() => setShowAdd(true)}
            className="flex items-center gap-1 rounded-2xl bg-ink px-3 py-2 text-xs font-bold text-shell"
          >
            <Plus size={14} /> Ajouter
          </button>
        </div>
        <div className="mt-2 overflow-x-auto">
          <table className="w-full min-w-[560px] text-xs">
            <thead>
              <tr className="text-left text-[11px] uppercase text-muted">
                <th className="py-2">Date</th>
                <th className="py-2">Libellé</th>
                <th className="py-2">Catégorie</th>
                <th className="py-2">Véhicule</th>
                <th className="py-2 text-right">Montant</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {expenses.filter((expense) => {
                const needle = (headerQuery ?? '').trim().toLowerCase();
                if (needle === '') return true;
                return `${expense.label} ${expense.category} ${expense.date}`.toLowerCase().includes(needle);
              }).map((e) => (
                <tr key={e.id} className="border-t border-line">
                  <td className="py-2 text-muted">{e.date}</td>
                  <td className="py-2 font-semibold">{e.label}</td>
                  <td className="py-2">
                    <span className="rounded-full bg-shell px-2 py-0.5 text-[11px]">{e.category}</span>
                  </td>
                  <td className="py-2 text-muted">{vehicles.find((v) => v.id === e.vehicleId)?.name ?? '—'}</td>
                  <td className="py-2 text-right font-bold">{formatMoney(e.amount, cur)}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => { void api(`/api/expenses/${e.id}`, { method: 'DELETE' }).then(() => invalidate('expenses')).catch(() => {}); }}
                      className="rounded-lg p-1 text-muted hover:bg-red-50 hover:text-danger"
                      aria-label="Supprimer"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
              {expenses.length === 0 && (
                <tr>
                  <td colSpan={6} className="py-4 text-center text-muted">
                    Aucune dépense enregistrée.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {showAdd && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4">
          <div className="w-full max-w-md rounded-3xl bg-card p-5">
            <div className="flex items-center justify-between">
              <p className="text-sm font-bold">Nouvelle dépense</p>
              <button onClick={() => setShowAdd(false)} className="rounded-full bg-shell p-2" aria-label="Fermer">
                <X size={14} />
              </button>
            </div>
            <div className="mt-3 space-y-2">
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="Libellé (ex: Vidange Dacia Logan)"
                className="w-full rounded-2xl border border-line bg-shell px-3 py-2 text-xs"
              />
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder={`Montant (${currencySymbol(cur)})`}

                  className="rounded-2xl border border-line bg-shell px-3 py-2 text-xs"
                />
                <input
                  type="date"
                  value={date}
                  onChange={(e) => setDate(e.target.value)}
                  className="rounded-2xl border border-line bg-shell px-3 py-2 text-xs"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value as FleetExpense['category'])}
                  className="rounded-2xl border border-line bg-shell px-3 py-2 text-xs"
                >
                  {CATEGORIES.map((c) => (
                    <option key={c} value={c}>{c}</option>
                  ))}
                </select>
                <select
                  value={vehicleId}
                  onChange={(e) => setVehicleId(e.target.value)}
                  className="rounded-2xl border border-line bg-shell px-3 py-2 text-xs"
                >
                  <option value="">Sans véhicule</option>
                  {vehicles.map((v) => (
                    <option key={v.id} value={v.id}>{v.name}</option>
                  ))}
                </select>
              </div>
              <button onClick={addExpense} className="w-full rounded-2xl bg-ink py-2.5 text-xs font-bold text-shell">
                Enregistrer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
